import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";

// (OTWARTE) - usuwanie niewymiernosci przez sprzezenie:
// n / (a +/- r*sqrt(b))

const SQUAREFREE = [2, 3, 5, 6, 7, 10, 11];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    [x, y] = [y, x % y];
  }
  return x || 1;
}

function gcd3(a, b, c) {
  return gcd(gcd(a, b), c);
}

function coerceToString(val) {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (typeof val === "object" && val.target && "value" in val.target) {
    return String(val.target.value ?? "");
  }
  if (typeof val === "object" && "raw" in val) return String(val.raw ?? "");
  if (typeof val === "object" && "latex" in val) return String(val.latex ?? "");
  if (typeof val === "object" && "text" in val) return String(val.text ?? "");
  if (typeof val === "object" && "expr" in val) return String(val.expr ?? "");
  if (typeof val === "object" && "value" in val && typeof val.value !== "object") {
    return String(val.value ?? "");
  }
  return "";
}

function formatTermWithRoot(coeff, b) {
  if (coeff === 0) return "0";
  const abs = Math.abs(coeff);
  const root = abs === 1 ? `\\sqrt{${b}}` : `${abs}\\sqrt{${b}}`;
  return coeff < 0 ? `-${root}` : root;
}

function formatLinearRootExpr(aCoeff, bCoeff, b) {
  if (bCoeff === 0) return `${aCoeff}`;
  const rootPart = formatTermWithRoot(bCoeff, b);
  if (aCoeff === 0) return rootPart;
  if (bCoeff > 0) return `${aCoeff} + ${rootPart}`;
  return `${aCoeff} - ${formatTermWithRoot(-bCoeff, b)}`;
}

function formatFinalLatex({ A, B, b, D }) {
  const numLatex = formatLinearRootExpr(A, B, b);
  if (D === 1) return numLatex;
  return `\\frac{${numLatex}}{${D}}`;
}

function expectedNumericFromData(data) {
  const { n, a, sign, b, rootCoeff = 1 } = data || {};
  if (!n || !a || !b || !sign) return NaN;
  return n / (a + sign * rootCoeff * Math.sqrt(b));
}

function stripOuterParens(s) {
  let out = String(s ?? "");
  while (out.startsWith("(") && out.endsWith(")")) {
    let depth = 0;
    let ok = true;
    for (let i = 0; i < out.length; i += 1) {
      if (out[i] === "(") depth += 1;
      if (out[i] === ")") depth -= 1;
      if (depth === 0 && i < out.length - 1) {
        ok = false;
        break;
      }
    }
    if (!ok) break;
    out = out.slice(1, -1);
  }
  return out;
}

function normalizeCanonical(str) {
  return String(str ?? "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .replace(/[â’â€“]/g, "-")
    .replace(/\\\\sqrt/g, "\u221A")
    .replace(/\\sqrt/g, "\u221A")
    .replace(/sqrt/g, "\u221A")
    .replace(/âš/g, "\u221A")
    .replace(/\u221A\{([^}]+)\}/g, "\u221A$1")
    .replace(/\u221A\(([^)]+)\)/g, "\u221A$1")
    .trim();
}

function parseLinearRootPart(numRaw, bExpected) {
  const simplifyRoot = (coeff, radicand) => {
    if (!Number.isInteger(radicand) || radicand <= 0) return { coeff, radicand };
    let inside = radicand;
    let outside = 1;
    for (let k = 2; k * k <= inside; k += 1) {
      const sq = k * k;
      while (inside % sq === 0) {
        outside *= k;
        inside /= sq;
      }
    }
    return { coeff: coeff * outside, radicand: inside, changed: outside !== 1 };
  };

  // czysta liczba calkowita/rzeczywista
  if (/^[+-]?\d+(?:\.\d+)?$/.test(numRaw)) {
    return { A: Number(numRaw), B: 0, rootWasSimplified: false };
  }

  // czyste c√b (np. √5, 4√5, -√7)
  let m = numRaw.match(/^([+-]?\d*(?:\.\d+)?)?\u221A(\d+(?:\.\d+)?)$/);
  if (m) {
    const cRaw = m[1];
    const C = cRaw === "" || cRaw == null || cRaw === "+" ? 1 : cRaw === "-" ? -1 : Number(cRaw);
    const b = Number(m[2]);
    if (!Number.isFinite(C) || !Number.isFinite(b)) return null;
    const simplified = simplifyRoot(C, Number.isInteger(b) ? b : b);
    if (bExpected != null && Math.abs(simplified.radicand - bExpected) > 1e-12) return null;
    return { A: 0, B: simplified.coeff, rootWasSimplified: !!simplified.changed };
  }

  // a + c√b lub a - c√b
  m = numRaw.match(/^([+-]?\d+(?:\.\d+)?)([+-])(\d*(?:\.\d+)?)?\u221A(\d+(?:\.\d+)?)$/);
  if (m) {
    const A = Number(m[1]);
    const sign = m[2] === "+" ? 1 : -1;
    const cRaw = m[3];
    const C = cRaw === "" || cRaw == null ? 1 : Number(cRaw);
    const b = Number(m[4]);
    if (!Number.isFinite(A) || !Number.isFinite(C) || !Number.isFinite(b)) return null;
    const simplified = simplifyRoot(sign * C, Number.isInteger(b) ? b : b);
    if (bExpected != null && Math.abs(simplified.radicand - bExpected) > 1e-12) return null;
    return { A, B: simplified.coeff, rootWasSimplified: !!simplified.changed };
  }

  // c√b + a lub c√b - a
  m = numRaw.match(/^([+-]?\d*(?:\.\d+)?)?\u221A(\d+(?:\.\d+)?)([+-])(\d+(?:\.\d+)?)$/);
  if (m) {
    const cRaw = m[1];
    const C = cRaw === "" || cRaw == null || cRaw === "+" ? 1 : cRaw === "-" ? -1 : Number(cRaw);
    const b = Number(m[2]);
    const sign = m[3] === "+" ? 1 : -1;
    const A = sign * Number(m[4]);
    if (!Number.isFinite(A) || !Number.isFinite(C) || !Number.isFinite(b)) return null;
    const simplified = simplifyRoot(C, Number.isInteger(b) ? b : b);
    if (bExpected != null && Math.abs(simplified.radicand - bExpected) > 1e-12) return null;
    return { A, B: simplified.coeff, rootWasSimplified: !!simplified.changed };
  }

  return null;
}

function parseConjugateForm(raw, bExpected) {
  const src = normalizeCanonical(raw);
  if (!src) return null;
  const parts = src.split("/");
  if (parts.length > 2) return null;

  const numRaw = stripOuterParens(parts[0]);
  const denRaw = stripOuterParens(parts.length === 2 ? parts[1] : "1");
  const D = Number(denRaw);
  if (!Number.isFinite(D) || D === 0) return null;

  // Postac rozwinieta: A + Bâšb
  const simple = parseLinearRootPart(numRaw, bExpected);
  if (simple) return { ...simple, D };

  // Postac z wyciagnietym czynnikiem: k(...)
  const factored = numRaw.match(/^([+-]?\d+(?:\.\d+)?)\((.+)\)$/);
  if (factored) {
    const k = Number(factored[1]);
    const inner = stripOuterParens(factored[2]);
    const innerParsed = parseLinearRootPart(inner, bExpected);
    if (innerParsed && Number.isFinite(k)) {
      return {
        A: k * innerParsed.A,
        B: k * innerParsed.B,
        D,
        rootWasSimplified: !!innerParsed.rootWasSimplified,
      };
    }
  }

  return null;
}

function sameSimpleForm(left, right) {
  if (!left || !right) return false;
  const eps = 1e-10;
  return (
    Math.abs(left.A - right.A) < eps &&
    Math.abs(left.B - right.B) < eps &&
    Math.abs(left.D - right.D) < eps
  );
}

export function generateTask() {
  const n = pick([2, 3, 4, 5, 6, 7, 8, 9]);
  const sign = Math.random() < 0.5 ? 1 : -1; // 1 => +r*sqrt(b), -1 => -r*sqrt(b)
  const rootCoeff = pick([1, 2, 2, 3]);

  // Wybieramy a,b,r tak, by mianownik po sprzezeniu byl dodatni i sensowny: a^2 - r^2*b > 0
  const candidates = [];
  for (const b of SQUAREFREE) {
    for (const a of [2, 3, 4, 5, 6, 7, 8]) {
      const D = a * a - rootCoeff * rootCoeff * b;
      if (D > 0) candidates.push({ a, b, D });
    }
  }
  const chosen = pick(candidates);
  const a = chosen.a;
  const b = chosen.b;

  // Po mnozeniu przez sprzezenie:
  // n/(a + s*râb) * (a - s*râb)/(a - s*râb) = n(a - s*râb)/(a^2-r^2*b)
  let A = n * a;
  let B = -n * sign * rootCoeff;
  let D = a * a - rootCoeff * rootCoeff * b;

  // Upraszczamy wspolny czynnik w liczniku/mianowniku
  const g = gcd3(A, B, D);
  A /= g;
  B /= g;
  D /= g;

  const rootLatex = rootCoeff === 1 ? `\\sqrt{${b}}` : `${rootCoeff}\\sqrt{${b}}`;
  const denLatex = sign === 1 ? `${a}+${rootLatex}` : `${a}-${rootLatex}`;
  const exprLatex = `\\frac{${n}}{${denLatex}}`;
  const conjLatex = sign === 1 ? `${a}-${rootLatex}` : `${a}+${rootLatex}`;
  const correctLatex = formatFinalLatex({ A, B, b, D });

  return {
    type: "RationalizeConjugateDenominatorOpenTask",
    data: {
      n,
      a,
      b,
      rootCoeff,
      sign,
      D0: a * a - rootCoeff * rootCoeff * b,
      A,
      B,
      D,
      exprLatex,
      conjLatex,
      correctLatex,
    },
    tags: ["dzialania", "pierwiastki", "usuniecie-niewymiernosci", "sprzezenie"],
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>Usun niewymiernosc z mianownika:</p>
      <div style={{ marginTop: -6 }}>
        <BlockMath math={data.exprLatex} />
      </div>
      <p style={{ opacity: 0.85, marginTop: -6 }}>Zapisz wynik w najprostszej postaci.</p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=""
      previewPrefix="Podgląd:"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const raw = coerceToString(userAnswer);
  const parsed =
    typeof userAnswer === "object" && Number.isFinite(userAnswer?.parsed)
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  const expected = expectedNumericFromData(data);
  if (!Number.isFinite(parsed) || !Number.isFinite(expected)) return false;
  if (Math.abs(parsed - expected) >= 0.01) return false;

  const givenForm = parseConjugateForm(raw, data.b);
  const expectedForm = { A: data.A, B: data.B, D: data.D };
  if (sameSimpleForm(givenForm, expectedForm)) {
    if (givenForm?.rootWasSimplified) {
      return {
        correct: true,
        needsSimplification: true,
        messageType: "warning",
        message: `Wynik jest poprawny, ale mozna go uproscic. Najprostsza postac: $${data.correctLatex}$.`,
      };
    }
    return true;
  }

  if (!givenForm) return true;

  return {
    correct: true,
    needsSimplification: true,
    messageType: "warning",
    message: `Wynik jest poprawny, ale mozna go uproscic. Najprostsza postac: $${data.correctLatex}$.`,
  };
}

export function renderAnswer({ data }) {
  return <InlineMath math={data.correctLatex} />;
}

export function renderUserAnswer(userAnswer) {
  const s = coerceToString(userAnswer);
  if (!s || s.trim() === "") return <i>(brak odpowiedzi)</i>;
  const latex = formatLatexPreview(s, "");
  return <InlineMath math={latex ? `\\displaystyle ${latex}` : normalizeCanonical(s)} />;
}

export function renderExplanation({ data }) {
  const { n, a, b, sign, rootCoeff = 1, exprLatex, conjLatex, D0, correctLatex } = data;

  const rootLatex = rootCoeff === 1 ? `\\sqrt{${b}}` : `${rootCoeff}\\sqrt{${b}}`;
  const denLeft = sign === 1 ? `${a}+${rootLatex}` : `${a}-${rootLatex}`;
  const denRight = sign === 1 ? `${a}-${rootLatex}` : `${a}+${rootLatex}`;

  return (
    <div className="space-y-2">
      <p>
        Mnozymy licznik i mianownik przez wyrazenie sprzezone do mianownika:{" "}
        <InlineMath math={conjLatex} />.
      </p>
      <BlockMath math={`${exprLatex}\\cdot\\frac{${conjLatex}}{${conjLatex}}`} />
      <BlockMath math={`=\\frac{${n}\\left(${conjLatex}\\right)}{(${denLeft})(${denRight})}`} />
      <p>
        W mianowniku korzystamy z roznicy kwadratow:{" "}
        <InlineMath math={`(a+r\\sqrt{b})(a-r\\sqrt{b})=a^2-r^2b`} />.
      </p>
      <BlockMath
        math={`=\\frac{${n}\\left(${conjLatex}\\right)}{${a}^2-${rootCoeff}^2\\cdot${b}}=\\frac{${n}\\left(${conjLatex}\\right)}{${D0}}`}
      />
      <p>Po uproszczeniu:</p>
      <BlockMath math={correctLatex} />
    </div>
  );
}

